import { z } from 'zod';

import type {
  EClassModuleRecurrenceType,
  EClassRoomDisciplinesStatus,
  EClassType,
  ECourseModality,
  SaveClassModuleViewModel,
} from '@/shared/api/generated';

/**
 * Recorrência de uma disciplina no formulário. Espelha
 * `SaveClassModuleRecurrenceViewModel`, mas com campos não-nulos para uso na UI.
 */
export interface ClassModuleRecurrenceForm {
  readonly type: EClassModuleRecurrenceType;
  readonly interval: number;
  readonly weekDay: number | null;
  readonly monthDay: number | null;
  readonly startTime: string;
  readonly endTime: string;
}

/**
 * Aula gerada pelo backend (somente leitura). Exibida na linha expansível da
 * disciplina; não faz parte do payload de escrita.
 */
export interface ClassModuleLessonView {
  readonly id?: number;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly status?: EClassRoomDisciplinesStatus;
}

/**
 * Disciplina do módulo no formulário. Guarda IDs (payload) e rótulos (exibição),
 * além das aulas de leitura vindas do detalhe.
 */
export interface ClassModuleDisciplineForm {
  readonly disciplineId: number;
  readonly disciplineName: string;
  readonly classroomId: number | null;
  readonly classroomName: string;
  readonly teacherPersonId: number | null;
  readonly teacherName: string;
  readonly quantity: number;
  /** Tipo de aula (1 = Normal, 2 = Sob demanda). Default Normal. */
  readonly type: EClassType;
  readonly planningWithExam: boolean;
  readonly useMoveQA: boolean;
  readonly isVisualizationAttendance: boolean;
  readonly recurrences: readonly ClassModuleRecurrenceForm[];
  readonly lessons: readonly ClassModuleLessonView[];
}

/**
 * Turma vinculada ao módulo. `classStudentId` compõe `classStudentIds`; nome e
 * curso são apenas para exibição na tabela de turmas.
 */
export interface LinkedClassForm {
  readonly classStudentId: number;
  readonly name: string;
  readonly course: string;
}

/**
 * Schema do formulário de módulo. Validado por Zod (React Hook Form + resolver).
 *
 * NÃO derivado de `saveClassModuleViewModelSchema` (gerado pelo Kubb): aquele
 * schema é exportado com o cast `as unknown as ToZod<SaveClassModuleViewModel>`,
 * o que torna a shape do `ZodObject` uma ficção de tipo. Qualquer `.pick()` /
 * `.omit()` / `.extend()` sobre ele degrada o `z.infer` para `never` (quebra,
 * p.ex., o `useFieldArray`). Por isso o schema do formulário é definido aqui
 * diretamente — é também um formato distinto do payload (campos só de exibição
 * como `linkedClasses` e `disciplines` com rótulos/aulas, e obrigatoriedades de
 * UX). A conversão para o payload da API é feita por `buildSaveViewModel`.
 *
 * As mensagens de erro são resolvidas na UI a partir do namespace `classModuleManagement`.
 */
export const classModuleFormSchema = z.object({
  companyId: z.number().int().nullable(),
  modality: z.custom<ECourseModality>().nullable(),
  description: z.string().min(1),
  dateStart: z.string().min(1),
  dateFinish: z.string(),
  linkedClasses: z.array(z.custom<LinkedClassForm>()),
  disciplines: z.array(z.custom<ClassModuleDisciplineForm>()),
});

/**
 * Valores do formulário de módulo (create/edit).
 *
 * Os campos que compõem o payload de escrita são um subconjunto de
 * `SaveClassModuleViewModel` (ver `buildSaveViewModel` para o mapeamento). O tipo
 * abaixo intersecta com uma asserção nula que só é `unknown` (no-op) enquanto
 * `companyId | modality | description | dateStart | dateFinish | disciplines`
 * continuarem existindo no contrato gerado pelo Kubb — se um for renomeado/removido,
 * a asserção vira `never` e o `yarn type-check` quebra aqui. `linkedClasses` fica
 * de fora de propósito: na API vira `classStudentIds`.
 */
export type ClassModuleFormValues = z.infer<typeof classModuleFormSchema> &
  (
    | 'companyId'
    | 'modality'
    | 'description'
    | 'dateStart'
    | 'dateFinish'
    | 'disciplines' extends keyof SaveClassModuleViewModel
    ? unknown
    : never);
